import { CommerceClient, type MembershipProduct, type PaymentMethod, type PaymentOrder, type RechargePackage } from "../api/commerce";
import { CLIENT_BUSINESS_CODE } from "../api/business-codes";
import { getGameRuntimeConfig } from "../api/config";
import { ApiError } from "../api/http";
import {
  getModelMoonCost,
  getModelRuntimeState,
  getModelRuntimeStateLabel,
  getModelUnavailableReason,
  isModelCallable,
  isModelQuotaExhausted,
  isPaidModel,
  ModelClient,
  NoAvailableModelError,
  persistCurrentGameModelId,
  readPreferredModelId,
  SelectedModelUnavailableError,
  type ModelCategoryGroup,
  type ModelItem
} from "../api/models";
import { normalizeMembership, type MembershipState } from "../membership";
import { openGamePortal } from "../forum-auth";

export interface EntitlementPurchasePrompt {
  title: string;
  message: string;
  reasons: string[];
}

export interface EntitlementSnapshot {
  membership?: Partial<MembershipState>;
  models: ModelItem[];
  selectedModelId?: number;
}

export interface EntitlementPurchaseDialogOptions {
  token?: string;
  authMode?: string;
  initialTab?: EntitlementTab;
  accountUser?: unknown;
  accountMembership?: Partial<MembershipState>;
  prompt?: EntitlementPurchasePrompt;
  modelCenter?: EntitlementModelCenterOptions;
  onRequireAuth?: () => void;
  onOrderCreated?: (order: PaymentOrder) => void;
  onClose?: () => void;
}

export interface EntitlementModelCenterOptions {
  groups: ModelCategoryGroup[];
  selectedModelId?: number;
  currentModelName?: string;
  authMode?: string;
  membership?: Partial<MembershipState>;
  kicker?: string;
  title?: string;
  description?: string;
  emptyText?: string;
  modelTitle?: (model: ModelItem) => string;
  modelSubtitle?: (model: ModelItem) => string;
  onModelSelected?: (model: ModelItem) => void;
}

export interface EntitlementAccessDialogOptions {
  token: string;
  error?: unknown;
  authMode?: string;
  selectedModelId?: number;
  onRequireAuth?: () => void;
  onOrderCreated?: (order: PaymentOrder) => void;
}

export interface LlmEntitlementRecoverySession {
  token?: string;
  authMode?: string;
  user?: unknown;
  membership?: Partial<MembershipState>;
}

export interface LlmEntitlementRecoveryConfig {
  getSession: () => LlmEntitlementRecoverySession;
  onRequireAuth: () => void;
}

export type EntitlementTab = "overview" | "models" | "membership" | "recharge";
type PurchaseKind = "membership_plan" | "recharge_package";

const STYLE_ID = "apk-games-entitlement-dialog-style";
const ROOT_ID = "apk-games-entitlement-dialog-root";
const NOTICE_ROOT_ID = "apk-games-entitlement-notice-root";
const QUOTA_EXHAUSTED_ROOT_ID = "apk-games-quota-exhausted-root";
let llmEntitlementRecoveryConfig: LlmEntitlementRecoveryConfig | undefined;
const handledEntitlementErrors = new WeakSet<object>();

export type ConfiguredQuotaExhaustedReason =
  | "anonymous_free_entitlement_exhausted"
  | "member_quota_exhausted"
  | "free_member_quota_exhausted";

export interface ConfiguredQuotaExhaustedPopup {
  reason: ConfiguredQuotaExhaustedReason;
  title: string;
  content: string;
}

type QuotaRecoveryAction = "auth" | "models" | "membership";

// 身份只决定恢复动作和辅助标签，主标题与正文必须完整使用后台配置。
const QUOTA_RECOVERY_PRESENTATIONS: Record<ConfiguredQuotaExhaustedReason, {
  contextLabel: string;
  primaryLabel: string;
  primaryAction: QuotaRecoveryAction;
}> = {
  anonymous_free_entitlement_exhausted: {
    contextLabel: "试玩次数提醒",
    primaryLabel: "前往登录/注册",
    primaryAction: "auth"
  },
  member_quota_exhausted: {
    contextLabel: "会员权益提醒",
    primaryLabel: "切换模型",
    primaryAction: "models"
  },
  free_member_quota_exhausted: {
    contextLabel: "免费会员权益提醒",
    primaryLabel: "查看会员",
    primaryAction: "membership"
  }
};

function remainingMoon(membership?: Partial<MembershipState>) {
  return typeof membership?.moonsBalance === "number" ? Math.max(0, membership.moonsBalance) : undefined;
}

function accountNickname(user?: unknown) {
  // 权益中心只能展示后端账号昵称，禁止使用角色名、企划名、游戏名或会员名称冒充账号。
  if (!user || typeof user !== "object") return "昵称未同步";
  const nickname = (user as { nickname?: unknown }).nickname;
  return typeof nickname === "string" && nickname.trim() ? nickname.trim() : "昵称未同步";
}

function remainingModelTimes(model?: ModelItem) {
  const remaining = model?.access?.remaining_times;
  return typeof remaining === "number" ? Math.max(0, remaining) : undefined;
}

function selectedModel(models: ModelItem[], selectedModelId?: number) {
  return models.find((model) => model.id === selectedModelId && isModelCallable(model))
    ?? models.find(isModelCallable)
    ?? models[0];
}

function requestedModel(models: ModelItem[], selectedModelId?: number) {
  return models.find((model) => model.id === selectedModelId) ?? models[0];
}

export function getEntitlementPurchasePrompt(snapshot: EntitlementSnapshot): EntitlementPurchasePrompt | undefined {
  const moonRemaining = remainingMoon(snapshot.membership);
  const model = requestedModel(snapshot.models, snapshot.selectedModelId);
  const callableModels = snapshot.models.filter(isModelCallable);
  if (model && getModelRuntimeState(model) === "crowded") {
    return {
      title: "模型当前拥挤",
      message: "当前模型暂不可用，请切换其他模型后重试。",
      reasons: callableModels.length ? ["拥挤状态暂停调用", "可切换其他模型"] : ["拥挤状态暂停调用", "请稍后再试"]
    };
  }
  const quotaExhausted = Boolean(model && !isPaidModel(model) && isModelQuotaExhausted(model));
  const allUnavailableFromQuota = snapshot.models.length > 0
    && callableModels.length === 0
    && snapshot.models.some((item) => !isPaidModel(item) && isModelQuotaExhausted(item));
  if (quotaExhausted || allUnavailableFromQuota) {
    return {
      title: "模型权益次数已用完",
      message: callableModels.length
        ? "当前模型权益次数已用完，请切换其他模型使用。"
        : "当前可用模型的权益次数已用完，可升级会员补充权益后继续使用。",
      reasons: ["本次不会扣月亮", callableModels.length ? "可切换其他模型" : "可升级会员权益"]
    };
  }

  const moonCost = model ? getModelMoonCost(model) : 0;
  const needsMoon = Boolean(model
    && isPaidModel(model)
    && (model.access?.billing_status === "paid" || remainingModelTimes(model) === 0));
  if (needsMoon && moonRemaining !== undefined && moonRemaining < moonCost) {
    return {
      title: "月亮余额不足",
      message: "月亮余额小于所需消耗，需补充后再进行操作。",
      reasons: [`本次需要 ${moonCost} 月亮`, `当前余额 ${moonRemaining} 月亮`]
    };
  }

  return undefined;
}

export function isEntitlementAccessError(error: unknown) {
  if (error instanceof NoAvailableModelError || error instanceof SelectedModelUnavailableError) return true;
  if (error instanceof ApiError) {
    // 权益恢复只认服务端业务码，HTTP 状态不再承担业务分支职责。
    return error.code === CLIENT_BUSINESS_CODE.RESOURCE_NOT_FOUND
      || error.code === CLIENT_BUSINESS_CODE.MOON_BALANCE_INSUFFICIENT
      || error.code === CLIENT_BUSINESS_CODE.MODEL_USAGE_EXHAUSTED
      || error.code === CLIENT_BUSINESS_CODE.MODEL_CROWDED;
  }
  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.includes("月亮余额")
    || message.includes("模型当前拥挤")
    || message.toLowerCase().includes("model is crowded")
    || message.includes("模型权益次数已用完")
    || message.includes("模型次数已用完")
    || message.includes("次数不足")
    || message.toLowerCase().includes("payment required");
}

function readNonEmptyString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

// 同时解析 completion 错误与模型预校验错误，保证两条失败路径展示完全一致。
export function readConfiguredQuotaExhaustedPopup(error: unknown): ConfiguredQuotaExhaustedPopup | undefined {
  let reason: unknown;
  let popup: unknown;
  let fallbackContent: string | undefined;
  if (error instanceof ApiError && error.code === CLIENT_BUSINESS_CODE.MODEL_USAGE_EXHAUSTED) {
    const data = error.payload && typeof error.payload === "object"
      ? (error.payload as Record<string, unknown>).data
      : undefined;
    if (!data || typeof data !== "object") return undefined;
    const dataRecord = data as Record<string, unknown>;
    reason = dataRecord.reason;
    popup = dataRecord.popup;
    fallbackContent = readNonEmptyString(error.message);
  } else if (error instanceof SelectedModelUnavailableError) {
    // 已选免费模型在预校验阶段用尽时，请求尚未发送，必须改读模型中心附带的同一份运营配置。
    if (isPaidModel(error.model) || !isModelQuotaExhausted(error.model)) return undefined;
    const configuredPopup = error.modelCenter.quotaExhaustedPopup;
    popup = configuredPopup;
    reason = configuredPopup?.reason;
  } else if (error instanceof NoAvailableModelError) {
    const models = flattenModelGroups(error.modelCenter.models);
    if (!models.some((model) => !isPaidModel(model) && isModelQuotaExhausted(model))) return undefined;
    const configuredPopup = error.modelCenter.quotaExhaustedPopup;
    popup = configuredPopup;
    reason = configuredPopup?.reason;
  } else {
    return undefined;
  }

  if (typeof reason !== "string" || !(reason in QUOTA_RECOVERY_PRESENTATIONS)) return undefined;
  if (!popup || typeof popup !== "object") return undefined;
  const popupRecord = popup as Record<string, unknown>;
  const title = readNonEmptyString(popupRecord.title);
  const content = readNonEmptyString(popupRecord.content) ?? fallbackContent;
  // 标题缺失时不能伪造运营配置；回退到通用权益提示，正文则允许沿用同一响应的 message。
  if (!title || !content) return undefined;
  return { reason: reason as ConfiguredQuotaExhaustedReason, title, content };
}

export function isAnonymousFreeEntitlementError(error: unknown) {
  return readConfiguredQuotaExhaustedPopup(error)?.reason === "anonymous_free_entitlement_exhausted";
}

function isHandledEntitlementError(error: unknown) {
  return Boolean(error && typeof error === "object" && handledEntitlementErrors.has(error));
}

function markEntitlementErrorHandled(error: unknown) {
  if (error && typeof error === "object") handledEntitlementErrors.add(error);
}

function unmarkEntitlementErrorHandled(error: unknown) {
  if (error && typeof error === "object") handledEntitlementErrors.delete(error);
}

export async function showEntitlementDialogForAccessError(options: EntitlementAccessDialogOptions) {
  if (!options.token || typeof document === "undefined") return false;
  if (isHandledEntitlementError(options.error)) return true;

  const configuredPopup = readConfiguredQuotaExhaustedPopup(options.error);
  if (configuredPopup) {
    markEntitlementErrorHandled(options.error);
    showConfiguredQuotaExhaustedRecovery(configuredPopup, options.selectedModelId);
    return true;
  }

  // 普通模型次数耗尽与月亮不足都必须先展示恢复提示，让玩家自行选择切换模型、
  // 升级会员或充值；不能依据登录方式直接跳进购买页。先标记可避免玩法层重复处理
  // 同一个 LLM 错误时并发拉取模型列表、叠加两层提示。
  markEntitlementErrorHandled(options.error);
  let center: Awaited<ReturnType<typeof ModelClient.list>>;
  try {
    center = options.error instanceof NoAvailableModelError || options.error instanceof SelectedModelUnavailableError
      ? options.error.modelCenter
      : await ModelClient.list({ token: options.token });
  } catch {
    unmarkEntitlementErrorHandled(options.error);
    return false;
  }
  const models = flattenModelGroups(center.models);
  const requested = requestedModel(models, options.selectedModelId);
  const rawMessage = options.error instanceof Error ? options.error.message : String(options.error ?? "");
  const moonInsufficient = options.error instanceof ApiError
    ? options.error.code === CLIENT_BUSINESS_CODE.MOON_BALANCE_INSUFFICIENT
    : rawMessage.includes("月亮余额") || rawMessage.toLowerCase().includes("payment required");
  const prompt = moonInsufficient
    ? {
        title: "月亮余额不足",
        message: "月亮余额小于所需消耗，需补充后再进行操作。",
        reasons: requested && isPaidModel(requested) ? [`本次需要 ${getModelMoonCost(requested)} 月亮`] : ["付费模型需消耗月亮"]
      }
    : getEntitlementPurchasePrompt({
        membership: center.membership,
        models,
        selectedModelId: options.selectedModelId
      }) ?? {
        title: "模型权益暂不可用",
        message: rawMessage || "当前模型暂不可用，请切换其他模型后重试。",
        reasons: ["可切换模型重试"]
      };
  const hasCallableModel = models.some(isModelCallable);
  showEntitlementNotice({
    prompt,
    primaryLabel: moonInsufficient ? "充值月亮" : hasCallableModel ? "切换模型" : "查看会员",
    secondaryLabel: moonInsufficient ? "查看模型" : "升级会员",
    onPrimary: () => openGamePortal({
      target: moonInsufficient ? "recharge" : hasCallableModel ? "models" : "membership",
      selectedModelId: options.selectedModelId,
    }),
    onSecondary: () => openGamePortal({
      target: moonInsufficient ? "models" : "membership",
      selectedModelId: options.selectedModelId,
    })
  });
  return true;
}

export async function showConfiguredEntitlementDialogForAccessError(error: unknown, token: string, selectedModelId?: number) {
  if (!isEntitlementAccessError(error) || isHandledEntitlementError(error)) return false;
  const session = llmEntitlementRecoveryConfig?.getSession();
  const effectiveToken = session?.token || token;
  if (!effectiveToken) return false;

  // LLM 共享出口读取当前游戏注册的会话，保证所有游戏按同一身份规则恢复额度。
  return showEntitlementDialogForAccessError({
    token: effectiveToken,
    error,
    authMode: session?.authMode,
    selectedModelId,
    onRequireAuth: llmEntitlementRecoveryConfig?.onRequireAuth
  });
}

function formatMoney(value?: number) {
  return `¥${(value ?? 0).toFixed(2)}`;
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizePlanType(plan: MembershipProduct) {
  const value = (plan.plan_type ?? "").toLowerCase();
  if (value) return value;
  if (plan.type === 3) return "free_member";
  if (plan.type === 4) return "free_product";
  if (plan.type === 2) return "planned_member";
  return "unlimited_member";
}

function visibleMembershipProducts(plans: MembershipProduct[]) {
  return plans
    .filter((plan) => plan.status !== 0 && plan.sale_enabled !== false && plan.is_sale !== false && plan.enabled !== false)
    .sort((left, right) => {
      const priority = (plan: MembershipProduct) => {
        const type = normalizePlanType(plan);
        if (type === "free_product") return 0;
        if (type === "free_member" || type === "free") return 1;
        if (type === "planned_member") return 2;
        return 3;
      };
      return priority(left) - priority(right) || (right.sort ?? 0) - (left.sort ?? 0) || left.id - right.id;
    });
}

function visibleRechargePackages(packages: RechargePackage[]) {
  return packages
    .filter((item) => item.enabled !== false && item.amount > 0)
    .sort((left, right) => (right.sort ?? 0) - (left.sort ?? 0) || left.pay_fee - right.pay_fee || left.id - right.id);
}

function planSummary(plan: MembershipProduct) {
  const parts = [
    plan.daily_model_free_quota ? `每日 ${plan.daily_model_free_quota} 次模型权益` : "",
    plan.gift_moons ? `赠 ${plan.gift_moons} 月亮` : "",
    plan.cycle_gift_moons ? `周期 ${plan.cycle_gift_moons} 月亮` : "",
    plan.expire_text || (plan.duration_days ? `${plan.duration_days} 天` : "")
  ].filter(Boolean);
  return parts.join(" · ") || plan.description || "会员权益商品";
}

function rechargeSummary(item: RechargePackage) {
  return item.description || `充值 ${item.amount} 月亮`;
}

function productName(kind: PurchaseKind, product?: MembershipProduct | RechargePackage) {
  if (!product) return "未选择商品";
  if (kind === "membership_plan") {
    const plan = product as MembershipProduct;
    return plan.name || plan.badge_name || "会员商品";
  }
  return product.name || "月亮充值包";
}

function productPrice(kind: PurchaseKind, product?: MembershipProduct | RechargePackage) {
  if (!product) return 0;
  return kind === "membership_plan" ? (product as MembershipProduct).price_amount : (product as RechargePackage).pay_fee;
}

function productDescription(kind: PurchaseKind, product?: MembershipProduct | RechargePackage) {
  if (!product) return "";
  return kind === "membership_plan" ? planSummary(product as MembershipProduct) : rechargeSummary(product as RechargePackage);
}

function productPrimaryValue(kind: PurchaseKind, product: MembershipProduct | RechargePackage) {
  if (kind === "membership_plan") {
    const plan = product as MembershipProduct;
    return plan.gift_moons || plan.cycle_gift_moons || plan.daily_model_free_quota || plan.duration_days || 0;
  }
  return (product as RechargePackage).amount;
}

function productPrimaryUnit(kind: PurchaseKind, product: MembershipProduct | RechargePackage) {
  if (kind === "recharge_package") return "月亮";
  const plan = product as MembershipProduct;
  if (plan.gift_moons || plan.cycle_gift_moons) return "月亮";
  if (plan.daily_model_free_quota) return "次/日";
  return "天";
}

function paymentMethodText(method: PaymentMethod) {
  return method.description || (method.channel ? `使用${method.name || method.channel}支付` : "立即拉起支付");
}

function flattenModelGroups(groups: ModelCategoryGroup[]) {
  return groups.flatMap((primary) =>
    primary.children.flatMap((secondary) =>
      secondary.models.map((model) => ({
        ...model,
        primary_category_name: model.primary_category_name || primary.name,
        secondary_category_name: model.secondary_category_name || secondary.name
      }))
    )
  );
}

function countGroupModels(group: ModelCategoryGroup) {
  return group.children.reduce((total, child) => total + child.models.length, 0);
}

function defaultModelTitle(model: ModelItem) {
  return model.display_name || model.dedicated_label || model.model_name || `模型 ${model.id}`;
}

function defaultModelSubtitle(model: ModelItem) {
  return [model.description, model.primary_category_name, model.secondary_category_name].filter(Boolean).slice(0, 2).join(" · ") || model.model_name || "用于 AI 叙事";
}

function modelQuotaLabel(model: ModelItem) {
  const remaining = remainingModelTimes(model);
  const used = model.access?.used_times;
  if (remaining === undefined) return model.access?.billing_status === "membership_tag_free" ? "权益次数充足" : "";
  const total = typeof used === "number" ? Math.max(0, used) + remaining : undefined;
  return total === undefined ? `权益剩余 ${remaining} 次` : `权益剩余 ${remaining}/${total} 次`;
}

function modelUsageRule(model: ModelItem) {
  const cost = getModelMoonCost(model);
  const status = model.access?.billing_status;
  if (status === "membership_tag_free") {
    return isPaidModel(model) ? `次数用完后 ${cost} 月亮/次` : "次数用完后需切换模型";
  }
  if (status === "paid") return `${cost} 月亮/次`;
  if (status === "free") return "当前模型免费";
  if (isModelQuotaExhausted(model)) return "权益次数已用完";
  return model.access?.label || "";
}

function modelBillingLabel(model: ModelItem) {
  if (getModelRuntimeState(model) === "crowded") return "不可用";
  const status = model.access?.billing_status;
  if (!status) return isPaidModel(model) ? `${getModelMoonCost(model)}月亮/次` : "免费";
  if (status === "membership_tag_free") {
    const remaining = remainingModelTimes(model);
    return remaining === undefined ? "权益优先" : `剩余 ${remaining} 次`;
  }
  if (status === "free") return "免费";
  if (status === "paid" || status === "planned_member_paid") return `${getModelMoonCost(model)}月亮/次`;
  if (status === "disabled") return "停用";
  if (status === "unavailable") return isModelQuotaExhausted(model) ? "次数用完" : "不可用";
  return model.access?.label || "可用";
}

function modelAccessReason(model: ModelItem) {
  return getModelUnavailableReason(model);
}

function compactDailyLimitText(membership?: Partial<MembershipState>) {
  if (!membership || membership.dailyFreeLimit === undefined) return "--";
  const used = membership.usedFreeToday ?? 0;
  const clampedUsed = Math.min(Math.max(0, used), membership.dailyFreeLimit);
  return clampedUsed >= membership.dailyFreeLimit ? `${clampedUsed}/${membership.dailyFreeLimit} 已用完` : `${clampedUsed}/${membership.dailyFreeLimit} 已用`;
}

function formatMembershipExpiry(membership?: Partial<MembershipState>) {
  if (!membership?.isMember) return "长期有效";
  if (!membership.expiresAt) return "长期有效";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit" }).format(membership.expiresAt);
}

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .apk-entitlement-layer{position:fixed;inset:0;z-index:9999;display:grid;align-items:stretch;justify-items:center;min-height:100vh;min-height:100dvh;overflow:hidden;background:var(--apk-entitlement-layer-bg,#edf3f9);backdrop-filter:blur(var(--apk-entitlement-layer-blur,0));font-family:var(--apk-entitlement-font,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif)}
    .apk-entitlement-notice-layer{position:fixed;inset:0;z-index:10000;display:grid;place-items:center;min-height:100vh;min-height:100dvh;padding:24px;background:rgba(5,9,16,.38);font-family:var(--apk-entitlement-font,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif)}
    .apk-entitlement-notice{position:relative;display:grid;gap:16px;width:min(100%,320px);padding:58px 22px 20px;border:1px solid rgba(255,255,255,.1);border-radius:16px;background:rgba(20,23,29,.92);color:#fff;box-shadow:0 24px 80px rgba(0,0,0,.34)}
    .apk-entitlement-notice-close{position:absolute;top:10px;left:50%;display:grid;place-items:center;width:44px;height:44px;padding:0;transform:translateX(-50%);border:0;background:transparent;color:#fff;font-size:42px;font-weight:200;line-height:1}
    .apk-entitlement-notice-copy{display:grid;grid-template-columns:24px minmax(0,1fr);gap:10px;align-items:start}
    .apk-entitlement-notice-copy i{color:#ffb53d;font-style:normal;font-size:20px;line-height:1.25}
    .apk-entitlement-notice-copy strong{display:block;font-size:17px;line-height:1.45;overflow-wrap:anywhere}
    .apk-entitlement-notice-copy p{margin:5px 0 0;color:rgba(255,255,255,.72);font-size:13px;line-height:1.5;overflow-wrap:anywhere}
    .apk-entitlement-notice-actions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
    .apk-entitlement-notice-actions button{min-height:44px;padding:0 12px;border:1px solid rgba(255,255,255,.18);border-radius:12px;background:rgba(255,255,255,.08);color:#fff;font-size:14px;font-weight:850}
    .apk-entitlement-notice-actions button[data-primary="true"]{border-color:#ffb44c;background:#ffad3d;color:#231506}
    .apk-llm-limit-layer{position:fixed;inset:0;z-index:10001;box-sizing:border-box;display:grid;align-items:end;justify-items:center;min-height:100vh;min-height:100dvh;padding:12px 12px max(12px,env(safe-area-inset-bottom,0px));overflow:hidden;background:rgba(18,27,34,.38);backdrop-filter:blur(2px);font-family:var(--apk-entitlement-font,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif)}
    .apk-llm-limit-sheet{position:relative;box-sizing:border-box;display:grid;gap:16px;width:min(100%,430px);max-height:calc(100vh - 24px);max-height:calc(100dvh - 24px);padding:22px 16px 16px;border:1px solid rgba(111,77,20,.12);border-radius:24px 24px 18px 18px;outline:none;background:radial-gradient(circle at 100% 0%,rgba(250,195,96,.22),transparent 38%),#fff;color:#17242d;box-shadow:0 24px 64px rgba(22,34,43,.28);animation:apk-llm-limit-enter .2s ease-out;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:none;-webkit-overflow-scrolling:touch}
    .apk-llm-limit-sheet::-webkit-scrollbar{display:none}
    .apk-llm-limit-sheet::before{position:absolute;inset:0 0 auto;height:4px;background:linear-gradient(90deg,#f2ad3d,#ffd98a);content:""}
    .apk-llm-limit-close{position:absolute;top:8px;right:8px;display:grid;place-items:center;width:44px;height:44px;padding:0;border:0;border-radius:999px;background:transparent;color:#68737b}
    .apk-llm-limit-close svg{width:20px;height:20px}
    .apk-llm-limit-heading{display:grid;grid-template-columns:48px minmax(0,1fr);align-items:center;gap:12px;padding-right:38px}
    .apk-llm-limit-icon{display:grid;place-items:center;width:48px;height:48px;border-radius:15px;background:linear-gradient(145deg,#fff4d6,#ffe4a2);color:#9a6000;box-shadow:inset 0 0 0 1px rgba(190,123,13,.1)}
    .apk-llm-limit-icon svg{width:25px;height:25px}
    .apk-llm-limit-title{display:grid;gap:3px;min-width:0}
    .apk-llm-limit-title small{color:#8b5b0d;font-size:12px;font-weight:800;line-height:1.3}
    .apk-llm-limit-title strong{color:#17242d;font-size:18px;font-weight:900;line-height:1.3;overflow-wrap:anywhere}
    .apk-llm-limit-copy{margin:0;color:#4f5d66;font-size:16px;font-weight:500;line-height:1.6;overflow-wrap:anywhere}
    .apk-llm-limit-actions{display:grid;grid-template-columns:1fr;gap:10px}
    .apk-llm-limit-actions button{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-width:0;min-height:48px;padding:0 12px;border:0;border-radius:14px;font-size:15px;font-weight:850;line-height:1.2;transition:filter .16s ease,transform .16s ease}
    .apk-llm-limit-actions button:first-child{border:1px solid #dce3e8;background:#f4f6f8;color:#52606a}
    .apk-llm-limit-actions button:last-child{background:#07b85a;color:#fff}
    .apk-llm-limit-actions button:active{transform:scale(.98);filter:brightness(.96)}
    .apk-llm-limit-actions button:focus-visible,.apk-llm-limit-close:focus-visible{outline:3px solid rgba(26,115,232,.42);outline-offset:2px}
    .apk-llm-limit-actions svg{width:18px;height:18px;flex:0 0 auto}
    @keyframes apk-llm-limit-enter{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
    .apk-entitlement-page{position:relative;display:grid;grid-template-rows:auto auto minmax(0,1fr) auto;min-width:0;min-height:0;width:min(100vw,var(--apk-entitlement-page-width,430px));height:100vh;height:100dvh;max-height:100vh;max-height:100dvh;overflow:hidden;background:var(--apk-entitlement-page-bg,var(--apk-entitlement-dialog-bg,#f4f7fb));color:var(--apk-entitlement-text,#172033);box-shadow:var(--apk-entitlement-shadow,0 18px 70px rgba(31,53,82,.26))}
    .apk-entitlement-topbar{display:grid;grid-template-columns:52px 1fr 52px;align-items:center;min-height:calc(56px + env(safe-area-inset-top,0px));padding:env(safe-area-inset-top,0px) 8px 0;background:var(--apk-entitlement-topbar-bg,rgba(255,255,255,.76));border-bottom:1px solid var(--apk-entitlement-line,rgba(34,56,84,.08));backdrop-filter:blur(14px)}
    .apk-entitlement-nav-btn{display:grid;place-items:center;width:44px;height:44px;padding:0;border:0;border-radius:999px;background:transparent;color:var(--apk-entitlement-heading,#111827);font-size:30px;line-height:1}
    .apk-entitlement-nav-btn span{transform:translateY(-1px)}
    .apk-entitlement-title{margin:0;color:var(--apk-entitlement-heading,#111827);font-size:20px;font-weight:850;line-height:1;text-align:center}
    .apk-entitlement-tabs{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));min-height:50px;background:var(--apk-entitlement-tabs-bg,rgba(255,255,255,.86));border-bottom:1px solid var(--apk-entitlement-line,rgba(34,56,84,.08))}
    .apk-entitlement-tab{position:relative;border:0;background:transparent;color:var(--apk-entitlement-tab-text,var(--apk-entitlement-heading,#1d2735));font-size:14px;font-weight:850}
    .apk-entitlement-tab[aria-selected="true"]{color:var(--apk-entitlement-tab-active,var(--apk-entitlement-accent,#f0835b))}
    .apk-entitlement-tab[aria-selected="true"]::after{content:"";position:absolute;left:28%;right:28%;bottom:0;height:4px;border-radius:999px;background:var(--apk-entitlement-tab-indicator,var(--apk-entitlement-accent,#f0835b))}
    .apk-entitlement-scroll-frame{position:relative;min-width:0;min-height:0;overflow:hidden}
    .apk-entitlement-scroll{width:100%;height:100%;min-width:0;min-height:0;overflow-x:hidden;overflow-y:scroll;padding:20px 16px calc(24px + env(safe-area-inset-bottom,0px));scrollbar-width:none;-webkit-overflow-scrolling:touch;overscroll-behavior-y:contain;touch-action:pan-y;cursor:grab;outline:none}
    .apk-entitlement-scroll:focus-visible{box-shadow:inset 0 0 0 2px var(--apk-entitlement-accent,#f0835b)}
    .apk-entitlement-scroll[data-dragging="true"]{cursor:grabbing;user-select:none}
    .apk-entitlement-scroll::-webkit-scrollbar{display:none}
    .apk-entitlement-scroll-edge{position:absolute;z-index:2;left:0;right:0;height:18px;opacity:0;pointer-events:none;transition:opacity .18s ease}
    .apk-entitlement-scroll-edge[data-edge="start"]{top:0;background:linear-gradient(180deg,rgba(0,0,0,.2),transparent)}
    .apk-entitlement-scroll-edge[data-edge="end"]{bottom:0;background:linear-gradient(0deg,rgba(0,0,0,.22),transparent)}
    .apk-entitlement-scroll-frame[data-can-scroll-up="true"] .apk-entitlement-scroll-edge[data-edge="start"],.apk-entitlement-scroll-frame[data-can-scroll-down="true"] .apk-entitlement-scroll-edge[data-edge="end"]{opacity:1}
    .apk-entitlement-scroll-track{position:absolute;z-index:3;top:10px;right:3px;bottom:10px;width:2px;border-radius:999px;background:var(--apk-entitlement-scroll-track,rgba(127,127,127,.12));opacity:0;pointer-events:none;transition:opacity .18s ease}
    .apk-entitlement-scroll-frame[data-scrollable="true"] .apk-entitlement-scroll-track{opacity:.58}
    .apk-entitlement-scroll-thumb{display:block;width:100%;min-height:24px;border-radius:inherit;background:var(--apk-entitlement-scroll-thumb,var(--apk-entitlement-accent,#f0835b));will-change:transform}
    .apk-entitlement-section{display:grid;gap:13px;margin:0 auto 22px;max-width:620px}
    .apk-entitlement-hero{display:grid;gap:8px}
    .apk-entitlement-kicker{color:var(--apk-entitlement-accent,#f0835b);font-size:13px;font-weight:900}
    .apk-entitlement-hero h2{margin:0;color:var(--apk-entitlement-heading,#111827);font-size:21px;line-height:1.18;font-weight:900;overflow-wrap:anywhere}
    .apk-entitlement-hero p{margin:0;color:var(--apk-entitlement-muted,#6f7886);font-size:14px;line-height:1.52;overflow-wrap:anywhere}
    .apk-entitlement-reasons{display:flex;flex-wrap:wrap;gap:8px;margin-top:3px}
    .apk-entitlement-reasons span{padding:7px 10px;border-radius:999px;background:var(--apk-entitlement-chip-bg,#eef5ff);color:var(--apk-entitlement-chip-text,#346ba4);font-size:12px;font-weight:850}
    .apk-entitlement-state{margin:0;padding:12px 14px;border:1px solid var(--apk-entitlement-state-border,transparent);border-radius:16px;background:var(--apk-entitlement-state-bg,rgba(52,107,164,.08));color:var(--apk-entitlement-state-text,#53687f);font-size:13px;line-height:1.45}
    .apk-entitlement-account-card{position:relative;display:grid;gap:18px;padding:var(--apk-entitlement-account-padding,8px 4px 2px);border:var(--apk-entitlement-account-border,0);border-radius:var(--apk-entitlement-account-radius,0);background:var(--apk-entitlement-account-bg,transparent);box-shadow:var(--apk-entitlement-account-shadow,none);overflow:visible}
    .apk-entitlement-account-card::before{content:"";position:absolute;left:0;top:0;width:var(--apk-entitlement-account-mark-width,0);height:var(--apk-entitlement-account-mark-height,0);border-radius:999px;background:var(--apk-entitlement-account-mark,var(--apk-entitlement-tab-indicator,var(--apk-entitlement-accent,#f0835b)));opacity:var(--apk-entitlement-account-mark-opacity,0);pointer-events:none}
    .apk-entitlement-account-card>*{position:relative}
    .apk-entitlement-account-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding-top:var(--apk-entitlement-account-head-padding-top,4px)}
    .apk-entitlement-account-head span{display:block;color:var(--apk-entitlement-muted,#6f7886);font-size:13px;font-weight:900}
    .apk-entitlement-account-head strong{display:block;margin-top:8px;color:var(--apk-entitlement-heading,#111827);font-size:24px;line-height:1.12;overflow-wrap:anywhere}
    .apk-entitlement-account-badge{flex:0 0 auto;padding:7px 0;border:0;border-radius:0;background:transparent;color:var(--apk-entitlement-secondary-text,var(--apk-entitlement-accent,#f0835b));font-size:13px;font-weight:900}
    .apk-entitlement-account-balance{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;padding:14px 0 16px;border-bottom:1px solid var(--apk-entitlement-account-rule,var(--apk-entitlement-line,rgba(34,56,84,.12)))}
    .apk-entitlement-account-balance span{color:var(--apk-entitlement-muted,#6f7886);font-size:13px;font-weight:900}
    .apk-entitlement-account-balance strong{color:var(--apk-entitlement-account-number,var(--apk-entitlement-heading,#111827));font-size:42px;line-height:.9;font-weight:950;overflow-wrap:anywhere}
    .apk-entitlement-benefit-list{display:grid;gap:0}
    .apk-entitlement-benefit-pill{display:flex;align-items:center;justify-content:space-between;gap:12px;min-width:0;padding:13px 0;border-radius:0;border-bottom:1px solid var(--apk-entitlement-account-rule,var(--apk-entitlement-line,rgba(34,56,84,.12)));background:transparent;color:var(--apk-entitlement-text,#172033)}
    .apk-entitlement-benefit-pill:last-child{border-bottom:0}
    .apk-entitlement-benefit-pill small{color:var(--apk-entitlement-muted,#6f7886);font-size:12px;font-weight:900;white-space:nowrap}
    .apk-entitlement-benefit-pill b{color:var(--apk-entitlement-heading,#111827);font-size:15px;line-height:1.15;font-weight:950;text-align:right;overflow-wrap:anywhere}
    .apk-entitlement-product-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    .apk-entitlement-product{position:relative;display:grid;align-content:start;gap:7px;min-height:154px;padding:17px 10px 13px;border:1px solid var(--apk-entitlement-plan-border,rgba(35,67,102,.1));border-radius:var(--apk-entitlement-card-radius,14px);background:var(--apk-entitlement-plan-bg,#fff);color:var(--apk-entitlement-text,#172033);text-align:center;box-shadow:var(--apk-entitlement-card-shadow,0 12px 30px rgba(47,75,112,.08));overflow:hidden}
    .apk-entitlement-product::before{content:"";position:absolute;inset:0 0 auto;height:4px;background:var(--apk-entitlement-product-ribbon,var(--apk-entitlement-accent,#f0835b));opacity:.78}
    .apk-entitlement-product[aria-pressed="true"]{border-color:var(--apk-entitlement-selected-border,var(--apk-entitlement-accent,#f0835b));box-shadow:var(--apk-entitlement-selected-shadow,0 16px 32px rgba(240,131,91,.22))}
    .apk-entitlement-product[aria-pressed="true"]::after{content:"";position:absolute;right:8px;top:8px;width:9px;height:9px;border-radius:999px;background:var(--apk-entitlement-accent,#f0835b);box-shadow:0 0 0 4px var(--apk-entitlement-selected-dot-bg,rgba(240,131,91,.14))}
    .apk-entitlement-product strong{color:var(--apk-entitlement-heading,#121827);font-size:14px;line-height:1.25;font-weight:900;overflow-wrap:anywhere}
    .apk-entitlement-product b{color:var(--apk-entitlement-accent,#f0835b);font-size:28px;line-height:1;font-weight:950;overflow-wrap:anywhere}
    .apk-entitlement-product em{color:var(--apk-entitlement-accent,#f0835b);font-style:normal;font-size:14px;font-weight:900}
    .apk-entitlement-product small{color:var(--apk-entitlement-muted,#6f7886);font-size:11px;line-height:1.35;overflow-wrap:anywhere}
    .apk-entitlement-product span{display:inline-grid;place-self:center;padding:6px 11px;border:1px solid var(--apk-entitlement-secondary-border,rgba(240,131,91,.24));border-radius:999px;color:var(--apk-entitlement-secondary-text,var(--apk-entitlement-accent,#f0835b));font-size:12px;font-weight:850}
    .apk-entitlement-payment{display:grid;gap:8px;padding:16px;border-radius:18px;background:var(--apk-entitlement-payment-bg,var(--apk-entitlement-control-bg,#fff));box-shadow:var(--apk-entitlement-payment-shadow,0 12px 30px rgba(47,75,112,.08))}
    .apk-entitlement-payment h3{margin:0 0 3px;color:var(--apk-entitlement-heading,#111827);font-size:16px}
    .apk-entitlement-method{display:grid;grid-template-columns:44px 1fr 28px;align-items:center;gap:10px;min-height:58px;padding:7px 0;border:0;border-top:1px solid var(--apk-entitlement-line,rgba(34,56,84,.08));background:transparent;color:var(--apk-entitlement-text,#172033);text-align:left}
    .apk-entitlement-method:first-of-type{border-top:0}
    .apk-entitlement-method i{display:grid;place-items:center;width:34px;height:34px;border-radius:999px;background:var(--apk-entitlement-method-icon-bg,#eaf7ef);color:var(--apk-entitlement-method-icon,#14b866);font-style:normal;font-weight:900}
    .apk-entitlement-method strong{display:block;color:var(--apk-entitlement-heading,#111827);font-size:15px}
    .apk-entitlement-method small{display:block;color:var(--apk-entitlement-muted,#6f7886);font-size:12px}
    .apk-entitlement-radio{width:22px;height:22px;border:2px solid var(--apk-entitlement-accent,#f0835b);border-radius:999px}
    .apk-entitlement-method[aria-pressed="true"] .apk-entitlement-radio{box-shadow:inset 0 0 0 5px var(--apk-entitlement-radio-bg,#fff);background:var(--apk-entitlement-accent,#f0835b)}
    .apk-entitlement-empty{display:grid;place-items:center;min-height:160px;padding:24px;border:1px dashed var(--apk-entitlement-plan-border,rgba(35,67,102,.18));border-radius:18px;color:var(--apk-entitlement-muted,#6f7886);text-align:center}
    .apk-entitlement-wallet{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    .apk-entitlement-wallet span{display:grid;align-content:center;justify-items:start;gap:4px;min-width:0;padding:10px;border:1px solid var(--apk-entitlement-plan-border,rgba(35,67,102,.1));border-radius:14px;background:var(--apk-entitlement-plan-bg,#fff);color:var(--apk-entitlement-muted,#6f7886);font-size:12px;font-weight:800}
    .apk-entitlement-wallet b{max-width:100%;color:var(--apk-entitlement-heading,#121827);font-size:14px;white-space:nowrap}
    .apk-entitlement-model-primary{display:flex;gap:8px;overflow-x:auto;overflow-y:hidden;padding:1px 0 3px;scrollbar-width:none}
    .apk-entitlement-model-primary::-webkit-scrollbar{display:none}
    .apk-entitlement-model-primary button{display:flex;align-items:center;gap:7px;min-height:36px;padding:0 12px;border:1px solid var(--apk-entitlement-plan-border,rgba(35,67,102,.12));border-radius:999px;background:var(--apk-entitlement-plan-bg,#fff);color:var(--apk-entitlement-text,#172033);font-weight:850;white-space:nowrap}
    .apk-entitlement-model-primary button[aria-pressed="true"]{border-color:var(--apk-entitlement-selected-border,var(--apk-entitlement-accent,#f0835b));color:var(--apk-entitlement-tab-active,var(--apk-entitlement-accent,#f0835b))}
    .apk-entitlement-model-primary b{display:grid;place-items:center;min-width:22px;height:22px;padding:0 7px;border-radius:999px;background:var(--apk-entitlement-chip-bg,#eef5ff);color:var(--apk-entitlement-chip-text,#346ba4);font-size:12px}
    .apk-entitlement-model-group{display:grid;gap:10px}
    .apk-entitlement-model-group > p{margin:0;color:var(--apk-entitlement-muted,#6f7886);font-size:13px;font-weight:850}
    .apk-entitlement-model-card-list{display:grid;gap:10px}
    .apk-entitlement-model-card{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:12px;width:100%;min-height:66px;padding:12px;border:1px solid var(--apk-entitlement-plan-border,rgba(35,67,102,.12));border-radius:14px;background:var(--apk-entitlement-plan-bg,#fff);color:var(--apk-entitlement-text,#172033);text-align:left;box-shadow:var(--apk-entitlement-card-shadow,0 12px 30px rgba(47,75,112,.08))}
    .apk-entitlement-model-card:disabled{cursor:not-allowed;opacity:.72}
    .apk-entitlement-model-card[aria-pressed="true"]{border-color:var(--apk-entitlement-selected-border,var(--apk-entitlement-accent,#f0835b));box-shadow:var(--apk-entitlement-selected-shadow,0 16px 32px rgba(240,131,91,.22))}
    .apk-entitlement-model-title{display:flex;flex-wrap:wrap;align-items:center;gap:7px;min-width:0}
    .apk-entitlement-model-title strong{display:block;color:var(--apk-entitlement-heading,#121827);font-size:15px;line-height:1.25;overflow-wrap:anywhere}
    .apk-entitlement-model-state{display:inline-flex;align-items:center;gap:5px;min-height:22px;padding:2px 8px;border:1px solid transparent;border-radius:999px;font-size:11px;font-weight:900;line-height:1;white-space:nowrap}
    .apk-entitlement-model-state::before{content:"";width:6px;height:6px;border-radius:999px;background:currentColor;box-shadow:0 0 0 3px rgba(15,157,111,.12)}
    .apk-entitlement-model-state[data-state="normal"]{border-color:var(--apk-entitlement-state-normal-border,rgba(15,157,111,.2));background:var(--apk-entitlement-state-normal-bg,#eaf8f2);color:var(--apk-entitlement-state-normal-text,#0b8a61)}
    .apk-entitlement-model-state[data-state="busy"]{border-color:var(--apk-entitlement-state-busy-border,rgba(181,117,23,.22));background:var(--apk-entitlement-state-busy-bg,#fff5dc);color:var(--apk-entitlement-state-busy-text,#a46309)}
    .apk-entitlement-model-state[data-state="busy"]::before{box-shadow:0 0 0 3px rgba(181,117,23,.12)}
    .apk-entitlement-model-state[data-state="crowded"]{border-color:var(--apk-entitlement-state-crowded-border,rgba(207,57,57,.2));background:var(--apk-entitlement-state-crowded-bg,#fff0f0);color:var(--apk-entitlement-state-crowded-text,#c43636)}
    .apk-entitlement-model-state[data-state="crowded"]::before{box-shadow:0 0 0 3px rgba(207,57,57,.12)}
    .apk-entitlement-model-card small{display:block;margin-top:4px;color:var(--apk-entitlement-muted,#6f7886);font-size:12px;line-height:1.35;overflow-wrap:anywhere}
    .apk-entitlement-model-usage{display:block;margin-top:6px;color:var(--apk-entitlement-chip-text,#346ba4);font-size:11px;font-weight:850;line-height:1.4;overflow-wrap:anywhere}
    .apk-entitlement-model-mark{display:flex;align-items:center;gap:5px;min-height:28px;padding:0 9px;border-radius:999px;background:var(--apk-entitlement-chip-bg,#eef5ff);color:var(--apk-entitlement-chip-text,#346ba4);font-size:12px;font-weight:900;white-space:nowrap}
    .apk-entitlement-model-card[aria-pressed="true"] .apk-entitlement-model-mark{background:var(--apk-entitlement-selected-dot-bg,rgba(240,131,91,.14));color:var(--apk-entitlement-tab-active,var(--apk-entitlement-accent,#f0835b))}
    .apk-entitlement-footer{position:relative;display:grid;grid-template-columns:minmax(0,1fr) minmax(138px,42%);align-items:center;gap:12px;padding:14px 16px calc(14px + env(safe-area-inset-bottom,0px));background:var(--apk-entitlement-footer-bg,rgba(255,255,255,.9));border-top:1px solid var(--apk-entitlement-line,rgba(34,56,84,.08));box-shadow:0 -14px 34px rgba(44,65,92,.1);backdrop-filter:blur(16px)}
    .apk-entitlement-total{display:flex;align-items:baseline;gap:5px;min-width:0;color:var(--apk-entitlement-heading,#111827);font-size:15px}
    .apk-entitlement-total b{color:var(--apk-entitlement-accent,#f0835b);font-size:26px;line-height:1;font-weight:950;overflow-wrap:anywhere}
    .apk-entitlement-primary{min-height:48px;border:0;border-radius:999px;background:var(--apk-entitlement-primary-bg,#ffad6b);color:var(--apk-entitlement-primary-text,#fff);font-size:17px;font-weight:900}
    .apk-entitlement-primary:disabled{opacity:.58}
    .apk-entitlement-secondary{width:100%;min-height:44px;border:1px solid var(--apk-entitlement-secondary-border,rgba(240,131,91,.24));border-radius:14px;background:var(--apk-entitlement-secondary-bg,#fff);color:var(--apk-entitlement-secondary-text,#f0835b);font-weight:900}
    @media (min-width:700px){.apk-entitlement-layer{align-items:center;padding:20px}.apk-entitlement-page{height:min(100vh,820px);height:min(100dvh,820px);max-height:820px;border:1px solid var(--apk-entitlement-dialog-border,rgba(255,255,255,.24));border-radius:var(--apk-entitlement-radius,22px);overflow:hidden}.apk-entitlement-topbar{min-height:56px;padding-top:0}.apk-llm-limit-layer{align-items:center;padding:24px}.apk-llm-limit-sheet{border-radius:24px}}
    @media (max-width:359px){.apk-entitlement-tab{font-size:12px}.apk-entitlement-product-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.apk-entitlement-footer{grid-template-columns:1fr}.apk-entitlement-primary{width:100%}.apk-entitlement-notice-actions{grid-template-columns:1fr}.apk-llm-limit-actions{grid-template-columns:1fr}}
    @media (prefers-reduced-motion:reduce){.apk-llm-limit-sheet{animation:none}.apk-llm-limit-actions button{transition:none}}
  `;
  document.head.appendChild(style);
}

function showEntitlementNotice(options: {
  prompt: EntitlementPurchasePrompt;
  primaryLabel: string;
  secondaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
}) {
  document.getElementById(NOTICE_ROOT_ID)?.remove();
  ensureStyle();
  const root = document.createElement("div");
  root.id = NOTICE_ROOT_ID;
  root.className = "apk-entitlement-notice-layer";
  root.innerHTML = `
    <section class="apk-entitlement-notice" role="alertdialog" aria-modal="true" aria-label="${escapeHtml(options.prompt.title)}">
      <button class="apk-entitlement-notice-close" type="button" data-notice-action="close" aria-label="关闭">×</button>
      <div class="apk-entitlement-notice-copy">
        <i aria-hidden="true">•</i>
        <div>
          <strong>${escapeHtml(options.prompt.message)}</strong>
          <p>${escapeHtml(options.prompt.reasons.join(" · "))}</p>
        </div>
      </div>
      <div class="apk-entitlement-notice-actions">
        <button type="button" data-notice-action="secondary">${escapeHtml(options.secondaryLabel)}</button>
        <button type="button" data-notice-action="primary" data-primary="true">${escapeHtml(options.primaryLabel)}</button>
      </div>
    </section>
  `;
  document.body.appendChild(root);

  const close = () => root.remove();
  root.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-notice-action]");
    if (!target) return;
    const action = target.dataset.noticeAction;
    close();
    if (action === "primary") options.onPrimary();
    if (action === "secondary") options.onSecondary();
  });
  return { close };
}

export function configureLlmEntitlementRecovery(config: LlmEntitlementRecoveryConfig) {
  llmEntitlementRecoveryConfig = config;
  // React 严格模式会重复挂载；仅允许当前注册者清理自身，避免旧清理函数误删新配置。
  return () => {
    if (llmEntitlementRecoveryConfig === config) llmEntitlementRecoveryConfig = undefined;
  };
}

export function showConfiguredQuotaExhaustedRecovery(
  popup: ConfiguredQuotaExhaustedPopup,
  selectedModelId?: number
) {
  if (typeof document === "undefined") return { close: () => undefined };
  const existing = document.getElementById(QUOTA_EXHAUSTED_ROOT_ID);
  if (existing) {
    // 并发叙事请求可能同时发现权益耗尽；复用当前弹层，避免重复遮罩和重复按钮。
    return {
      close: () => existing.querySelector<HTMLButtonElement>("[data-llm-limit-action='close']")?.click()
    };
  }

  ensureStyle();
  const presentation = QUOTA_RECOVERY_PRESENTATIONS[popup.reason];
  // 打开时保存原焦点，关闭后恢复玩家的键盘操作位置。
  const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
  const root = document.createElement("div");
  root.id = QUOTA_EXHAUSTED_ROOT_ID;
  root.className = "apk-llm-limit-layer";
  root.innerHTML = `
    <section class="apk-llm-limit-sheet" role="dialog" aria-modal="true" aria-labelledby="apk-llm-limit-title" aria-describedby="apk-llm-limit-description" tabindex="-1">
      <button class="apk-llm-limit-close" type="button" data-llm-limit-action="close" aria-label="暂时关闭提示">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
      <div class="apk-llm-limit-heading">
        <span class="apk-llm-limit-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <div class="apk-llm-limit-title">
          <small>${escapeHtml(presentation.contextLabel)}</small>
          <strong id="apk-llm-limit-title">${escapeHtml(popup.title)}</strong>
        </div>
      </div>
      <p class="apk-llm-limit-copy" id="apk-llm-limit-description">${escapeHtml(popup.content)}</p>
      <div class="apk-llm-limit-actions">
        <button type="button" data-llm-limit-action="close">
          <span>继续本地体验</span>
        </button>
        <button type="button" data-llm-limit-action="primary">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5m5 5H3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span>${escapeHtml(presentation.primaryLabel)}</span>
        </button>
      </div>
    </section>
  `;
  document.body.appendChild(root);

  const close = () => {
    document.removeEventListener("keydown", onKeydown);
    root.remove();
    previouslyFocused?.focus();
  };
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>("button:not([disabled]),[tabindex='0']"));
    if (!focusable.length) return;
    // 焦点必须留在阻断式弹层内，避免键盘用户误操作被遮罩覆盖的游戏按钮。
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener("keydown", onKeydown);
  root.querySelector<HTMLElement>(".apk-llm-limit-sheet")?.focus();
  root.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    if (target === root) {
      close();
      return;
    }
    const action = target?.closest<HTMLElement>("[data-llm-limit-action]")?.dataset.llmLimitAction;
    if (!action) return;
    if (action === "close") {
      close();
      return;
    }

    close();
    if (action !== "primary") return;
    // 三类用户共用运营文案，但恢复动作必须符合当前权益身份，不能把会员用户错误引导到登录页。
    if (presentation.primaryAction === "auth") {
      if (llmEntitlementRecoveryConfig?.onRequireAuth) {
        llmEntitlementRecoveryConfig.onRequireAuth();
      } else {
        openGamePortal({ target: "auth" });
      }
      return;
    }
    openGamePortal({
      target: presentation.primaryAction,
      selectedModelId
    });
  });
  return { close };
}

export function showAnonymousFreeEntitlementRecovery(_token?: string, message?: string) {
  // 直接调用者同样走配置化弹层结构；该入口只提供无服务端结构时的默认匿名提示。
  return showConfiguredQuotaExhaustedRecovery({
    reason: "anonymous_free_entitlement_exhausted",
    title: "免费体验次数已用完",
    content: message?.trim() || "免费会员权益次数已用完，请登录或注册账号后继续体验。"
  });
}

function openPayment(order: PaymentOrder) {
  const target = order.pay_url || order.pay_info;
  if (!target) return;
  if (order.pay_type === "urlscheme") {
    window.location.href = target;
    return;
  }
  window.open(target, "_blank", "noopener,noreferrer");
}

export function showEntitlementPurchaseDialog(options: EntitlementPurchaseDialogOptions = {}) {
  const target = options.initialTab === "recharge"
    ? "recharge"
    : options.initialTab === "membership"
      ? "membership"
      : "models";
  openGamePortal({ target, selectedModelId: options.modelCenter?.selectedModelId });
  return { close: () => undefined };

  /* c8 ignore start -- 旧内置中心暂留一版用于回滚，新入口不会再执行。 */
  if (typeof document === "undefined") return { close: () => undefined };
  document.getElementById(ROOT_ID)?.remove();
  document.getElementById(NOTICE_ROOT_ID)?.remove();
  ensureStyle();

  const root = document.createElement("div");
  root.id = ROOT_ID;
  root.className = "apk-entitlement-layer";
  document.body.appendChild(root);

  let closed = false;
  let activeTab: EntitlementTab = options.initialTab ?? "membership";
  let activeModelPrimaryId = 0;
  const centerModels = flattenModelGroups(options.modelCenter?.groups ?? []);
  const storedModelId = readPreferredModelId(getGameRuntimeConfig().packageId);
  const initialModel = centerModels.find((model) => model.id === storedModelId)
    ?? centerModels.find((model) => model.id === options.modelCenter?.selectedModelId)
    ?? selectedModel(centerModels);
  const initialCallableModel = initialModel && isModelCallable(initialModel) ? initialModel : undefined;
  let activeSelectedModelId = initialModel?.id ?? options.modelCenter?.selectedModelId ?? 0;
  let activeSelectedModelName = initialModel
    ? (options.modelCenter?.modelTitle ?? defaultModelTitle)(initialModel)
    : options.modelCenter?.currentModelName || "";
  if (initialCallableModel) {
    // 仅同步仍可用的既有选择；不可用模型留给用户显式切换，避免自动改成付费模型。
    persistCurrentGameModelId(initialCallableModel!.id);
    if (initialCallableModel!.id !== options.modelCenter?.selectedModelId) options.modelCenter?.onModelSelected?.(initialCallableModel!);
  }
  let products: MembershipProduct[] = [];
  let rechargePackages: RechargePackage[] = [];
  let methods: PaymentMethod[] = [];
  let selectedKind: PurchaseKind = "membership_plan";
  let selectedProductId = 0;
  let selectedMethodId = 0;
  let busy = false;
  let status = "正在同步会员商品...";
  const scrollPositions: Record<EntitlementTab, number> = {
    overview: 0,
    models: 0,
    membership: 0,
    recharge: 0
  };
  let renderedTab = activeTab;
  let suppressClickUntil = 0;
  let dragState: {
    pointerId: number;
    startX: number;
    startY: number;
    startScrollTop: number;
    moved: boolean;
    scroll: HTMLElement;
  } | undefined;

  const prompt = () => options.prompt ?? {
    title: "补充模型权益",
    message: "开通或升级会员可补充模型权益次数；付费模型在次数用完后按月亮消耗继续使用。",
    reasons: ["先用权益次数", "付费模型再扣月亮"]
  };

  const close = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener("keydown", onKeydown);
    root.remove();
    options.onClose?.();
  };

  function currentProducts() {
    return activeTab === "recharge" ? rechargePackages : products;
  }

  function currentKind(): PurchaseKind {
    return activeTab === "recharge" ? "recharge_package" : "membership_plan";
  }

  function selectedProduct() {
    if (activeTab === "overview") return undefined;
    const kind = currentKind();
    const item = currentProducts().find((product) => product.id === selectedProductId);
    if (item && selectedKind === kind) return item;
    return currentProducts()[0];
  }

  function canCreatePaymentOrder() {
    // 匿名注册已经生成普通用户账号，其会员购买与月亮充值权限不应被登录方式限制。
    return Boolean(options.token);
  }

  function authRequiredText() {
    return "请先创建用户账号后再购买会员或充值月亮。";
  }

  function ensureSelection() {
    if (activeTab === "overview" || activeTab === "models") return;
    const kind = currentKind();
    const items = currentProducts();
    if (!items.length) {
      selectedKind = kind;
      selectedProductId = 0;
      return;
    }
    if (selectedKind !== kind || !items.some((item) => item.id === selectedProductId)) {
      selectedKind = kind;
      selectedProductId = items[0].id;
    }
  }

  function renderTabs() {
    const tabs: Array<{ id: EntitlementTab; label: string }> = [
      { id: "overview", label: "我的权益" },
      { id: "models", label: "模型" },
      { id: "membership", label: "会员" },
      { id: "recharge", label: "月亮" }
    ];
    return tabs.map((tab) => `<button class="apk-entitlement-tab" type="button" data-tab="${tab.id}" aria-selected="${activeTab === tab.id}">${tab.label}</button>`).join("");
  }

  function renderHero() {
    const currentPrompt = prompt();
    if (activeTab === "recharge") {
      return `
        <section class="apk-entitlement-section apk-entitlement-hero">
          <span class="apk-entitlement-kicker">月亮充值套餐</span>
          <h2>补充月亮余额</h2>
          <p>充值月亮后可用于付费模型调用，适合临时继续 AI 叙事。</p>
        </section>
      `;
    }
    if (activeTab === "models") {
      const modelCenter = options.modelCenter;
      const currentModelName = activeSelectedModelName || modelCenter?.currentModelName || "系统默认模型";
      const description =
        modelCenter?.description && modelCenter.currentModelName && activeSelectedModelName
          ? modelCenter.description.replace(modelCenter.currentModelName, activeSelectedModelName)
          : modelCenter?.description || `${currentModelName} · 可查看权益次数与月亮消耗`;
      return `
        <section class="apk-entitlement-section apk-entitlement-hero">
          <span class="apk-entitlement-kicker">${escapeHtml(modelCenter?.kicker || "AI 模型")}</span>
          <h2>${escapeHtml(modelCenter?.title || "选择叙事模型")}</h2>
          <p>${escapeHtml(description)}</p>
        </section>
      `;
    }
    if (activeTab === "overview") {
      return "";
    }
    return `
      <section class="apk-entitlement-section apk-entitlement-hero">
        <span class="apk-entitlement-kicker">会员开通</span>
        <h2>${escapeHtml(currentPrompt.title)}</h2>
        <p>${escapeHtml(currentPrompt.message)}</p>
        <div class="apk-entitlement-reasons">${currentPrompt.reasons.map((item) => `<span>${escapeHtml(item)}</span>`).join("")}</div>
      </section>
    `;
  }

  function renderProduct(product: MembershipProduct | RechargePackage, kind: PurchaseKind) {
    const selected = selectedKind === kind && selectedProductId === product.id;
    return `
      <button class="apk-entitlement-product" type="button" data-kind="${kind}" data-product-id="${product.id}" aria-pressed="${selected}">
        <strong>${escapeHtml(productName(kind, product))}</strong>
        <b>${escapeHtml(String(productPrimaryValue(kind, product).toLocaleString("zh-CN")))}</b>
        <em>${escapeHtml(productPrimaryUnit(kind, product))}</em>
        <small>${escapeHtml(productDescription(kind, product))}</small>
        <span>${escapeHtml(formatMoney(productPrice(kind, product)))}</span>
      </button>
    `;
  }

  function renderProducts() {
    if (activeTab === "models") return "";
    if (activeTab === "overview") {
      const membership = normalizeMembership(options.accountMembership ?? options.modelCenter?.membership);
      const accountName = accountNickname(options.accountUser);
      const membershipTitle = membership?.title || "--";
      return `
        <section class="apk-entitlement-section">
          <div class="apk-entitlement-account-card" aria-label="当前账号权益">
            <header class="apk-entitlement-account-head">
              <div>
                <span>当前账号</span>
                <strong>${escapeHtml(accountName)}</strong>
              </div>
              <b class="apk-entitlement-account-badge">${escapeHtml(membershipTitle)}</b>
            </header>
            <div class="apk-entitlement-account-balance">
              <span>Moon 余额</span>
              <strong>${escapeHtml(membership?.moonsBalance ?? "--")}</strong>
            </div>
            <div class="apk-entitlement-benefit-list">
              <span class="apk-entitlement-benefit-pill">
                <small>会员状态</small>
                <b>${escapeHtml(membershipTitle)}</b>
              </span>
              <span class="apk-entitlement-benefit-pill">
                <small>有效期</small>
                <b>${escapeHtml(formatMembershipExpiry(membership))}</b>
              </span>
              <span class="apk-entitlement-benefit-pill">
                <small>会员日额度</small>
                <b>${escapeHtml(`${compactDailyLimitText(membership)} 次`)}</b>
              </span>
              <span class="apk-entitlement-benefit-pill">
                <small>调用规则</small>
                <b>权益次数优先</b>
              </span>
            </div>
          </div>
        </section>
      `;
    }
    const kind = currentKind();
    const items = currentProducts();
    return `
      <section class="apk-entitlement-section">
        ${!canCreatePaymentOrder() ? `<p class="apk-entitlement-state">${escapeHtml(authRequiredText())}</p>` : status ? `<p class="apk-entitlement-state">${escapeHtml(status)}</p>` : ""}
        ${items.length
          ? `<div class="apk-entitlement-product-grid" data-kind="${kind}">${items.map((item) => renderProduct(item, kind)).join("")}</div>`
          : `<div class="apk-entitlement-empty">暂无可购买商品，请稍后再试。</div>`}
      </section>
    `;
  }

  function renderModels() {
    if (activeTab !== "models") return "";
    const modelCenter = options.modelCenter;
    if (!modelCenter) {
      return `
        <section class="apk-entitlement-section">
          <div class="apk-entitlement-empty">暂无模型列表，请从游戏内模型入口打开。</div>
        </section>
      `;
    }
    const groups = modelCenter.groups
      .map((group) => ({
        ...group,
        children: group.children.filter((secondary) => secondary.models.length > 0)
      }))
      .filter((group) => group.children.length > 0);
    const models = flattenModelGroups(groups);
    if (!activeModelPrimaryId || !groups.some((group) => group.id === activeModelPrimaryId)) {
      const selectedPrimary = groups.find((group) =>
        group.children.some((secondary) => secondary.models.some((model) => model.id === activeSelectedModelId))
      );
      activeModelPrimaryId = selectedPrimary?.id ?? groups[0]?.id ?? 0;
    }
    const activeGroup = groups.find((group) => group.id === activeModelPrimaryId) ?? groups[0];
    const title = modelCenter.modelTitle ?? defaultModelTitle;
    const subtitle = modelCenter.modelSubtitle ?? defaultModelSubtitle;
    return `
      <section class="apk-entitlement-section">
        <div class="apk-entitlement-wallet" aria-label="AI 账户余量">
          <span>会员 <b>${escapeHtml(normalizeMembership(modelCenter.membership).title)}</b></span>
          <span>月亮 <b>${escapeHtml(modelCenter.membership?.moonsBalance ?? "--")}</b></span>
          <span>日额度 <b>${escapeHtml(compactDailyLimitText(modelCenter.membership))}</b></span>
        </div>
        ${groups.length ? `
          <nav class="apk-entitlement-model-primary" aria-label="模型一级分类">
            ${groups.map((group) => `
              <button type="button" data-model-primary-id="${group.id}" aria-pressed="${activeGroup?.id === group.id}">
                <span>${escapeHtml(group.name)}</span>
                <b>${countGroupModels(group)}</b>
              </button>
            `).join("")}
          </nav>
        ` : ""}
        ${models.length ? `
          <div class="apk-entitlement-model-card-list">
            ${activeGroup?.children.map((secondary) => `
              <section class="apk-entitlement-model-group">
                <p>${escapeHtml(secondary.name)} · ${secondary.models.length} 个模型</p>
                ${secondary.models.map((model) => {
                  const selected = activeSelectedModelId === model.id;
                  const locked = !isModelCallable(model);
                  const runtimeState = getModelRuntimeState(model);
                  return `
                    <button class="apk-entitlement-model-card" type="button" data-model-id="${model.id}" data-model-state="${runtimeState}" aria-pressed="${selected}" ${locked ? "disabled" : ""}>
                      <span>
                        <span class="apk-entitlement-model-title">
                          <strong>${escapeHtml(title(model))}</strong>
                          <span class="apk-entitlement-model-state" data-state="${runtimeState}">${escapeHtml(getModelRuntimeStateLabel(model))}</span>
                        </span>
                        <small>${escapeHtml(locked ? modelAccessReason(model) : subtitle(model))}</small>
                        <span class="apk-entitlement-model-usage">${escapeHtml([modelQuotaLabel(model), modelUsageRule(model)].filter(Boolean).join(" · "))}</span>
                      </span>
                      <span class="apk-entitlement-model-mark">${escapeHtml(selected ? `当前 · ${modelBillingLabel(model)}` : modelBillingLabel(model))}</span>
                    </button>
                  `;
                }).join("")}
              </section>
            `).join("") ?? ""}
          </div>
        ` : `<div class="apk-entitlement-empty">${escapeHtml(modelCenter.emptyText || "暂无可切换模型。")}</div>`}
      </section>
    `;
  }

  function renderPaymentMethods() {
    if (activeTab === "overview" || activeTab === "models") return "";
    if (!methods.length) {
      return `
        <section class="apk-entitlement-section apk-entitlement-payment">
          <h3>选择支付方式</h3>
          <p class="apk-entitlement-state">暂无可用支付方式，创建订单时将使用系统默认支付配置。</p>
        </section>
      `;
    }
    return `
      <section class="apk-entitlement-section apk-entitlement-payment">
        <h3>选择支付方式</h3>
        ${methods.map((method) => `
          <button class="apk-entitlement-method" type="button" data-method-id="${method.id}" aria-pressed="${selectedMethodId === method.id}">
            <i>${method.channel === "alipay" ? "支" : "✓"}</i>
            <span>
              <strong>${escapeHtml(method.name || method.channel || "支付方式")}</strong>
              <small>${escapeHtml(paymentMethodText(method))}</small>
            </span>
            <span class="apk-entitlement-radio" aria-hidden="true"></span>
          </button>
        `).join("")}
      </section>
    `;
  }

  function renderFooter() {
    if (activeTab === "overview" || activeTab === "models") return "";
    const kind = currentKind();
    const product = selectedProduct();
    const disabled = busy || !product;
    const paymentReady = canCreatePaymentOrder();
    return `
      <footer class="apk-entitlement-footer">
        <div class="apk-entitlement-total">
          <span>合计:</span>
          <b>${escapeHtml(formatMoney(productPrice(kind, product)))}</b>
        </div>
        ${paymentReady
          ? `<button class="apk-entitlement-primary" type="button" data-action="pay" ${disabled ? "disabled" : ""}>${busy ? "创建中..." : "立即支付"}</button>`
          : `<button class="apk-entitlement-primary" type="button" data-action="auth">登录/注册</button>`}
      </footer>
    `;
  }

  function rememberScrollPosition() {
    const scroll = root.querySelector<HTMLElement>(".apk-entitlement-scroll");
    if (scroll) scrollPositions[renderedTab] = scroll.scrollTop;
  }

  function updateScrollAffordance(scroll?: HTMLElement | null) {
    const activeScroll = scroll ?? root.querySelector<HTMLElement>(".apk-entitlement-scroll");
    const frame = activeScroll?.closest<HTMLElement>(".apk-entitlement-scroll-frame");
    const thumb = frame?.querySelector<HTMLElement>(".apk-entitlement-scroll-thumb");
    if (!activeScroll || !frame || !thumb) return;

    const maxScrollTop = Math.max(0, activeScroll.scrollHeight - activeScroll.clientHeight);
    const scrollable = maxScrollTop > 1;
    const canScrollUp = scrollable && activeScroll.scrollTop > 1;
    const canScrollDown = scrollable && activeScroll.scrollTop < maxScrollTop - 1;
    frame.dataset.scrollable = String(scrollable);
    frame.dataset.canScrollUp = String(canScrollUp);
    frame.dataset.canScrollDown = String(canScrollDown);

    if (!scrollable) {
      thumb.style.height = "100%";
      thumb.style.transform = "translate3d(0,0,0)";
      return;
    }

    const trackHeight = Math.max(0, frame.clientHeight - 20);
    const thumbHeight = Math.max(24, Math.round(trackHeight * activeScroll.clientHeight / activeScroll.scrollHeight));
    const travel = Math.max(0, trackHeight - thumbHeight);
    const offset = Math.round(travel * activeScroll.scrollTop / maxScrollTop);
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translate3d(0,${offset}px,0)`;
  }

  function restoreScrollPosition() {
    const scroll = root.querySelector<HTMLElement>(".apk-entitlement-scroll");
    if (!scroll) return;
    const maxScrollTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    scroll.scrollTop = Math.min(scrollPositions[activeTab], maxScrollTop);
    renderedTab = activeTab;
    updateScrollAffordance(scroll);
  }

  const render = () => {
    rememberScrollPosition();
    ensureSelection();
    root.innerHTML = `
      <section class="apk-entitlement-page" role="dialog" aria-modal="true" aria-label="模型与权益中心">
        <header class="apk-entitlement-topbar">
          <button class="apk-entitlement-nav-btn" type="button" data-action="close" aria-label="返回"><span>‹</span></button>
          <h1 class="apk-entitlement-title">模型与权益</h1>
          <button class="apk-entitlement-nav-btn" type="button" data-action="close" aria-label="关闭"><span>×</span></button>
        </header>
        <nav class="apk-entitlement-tabs" aria-label="会员中心栏目">${renderTabs()}</nav>
        <div class="apk-entitlement-scroll-frame" data-scrollable="false" data-can-scroll-up="false" data-can-scroll-down="false">
          <main class="apk-entitlement-scroll" tabindex="0" aria-label="权益中心内容">
            ${renderHero()}
            ${renderModels()}
            ${renderProducts()}
            ${renderPaymentMethods()}
          </main>
          <span class="apk-entitlement-scroll-edge" data-edge="start" aria-hidden="true"></span>
          <span class="apk-entitlement-scroll-edge" data-edge="end" aria-hidden="true"></span>
          <span class="apk-entitlement-scroll-track" aria-hidden="true"><i class="apk-entitlement-scroll-thumb"></i></span>
        </div>
        ${renderFooter()}
      </section>
    `;
    restoreScrollPosition();
  };

  function onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") close();
  }

  function onScroll(event: Event) {
    const scroll = event.target instanceof HTMLElement && event.target.matches(".apk-entitlement-scroll")
      ? event.target
      : undefined;
    if (!scroll) return;
    scrollPositions[renderedTab] = scroll.scrollTop;
    updateScrollAffordance(scroll);
  }

  function onWheel(event: WheelEvent) {
    if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const scroll = root.querySelector<HTMLElement>(".apk-entitlement-scroll");
    if (!scroll) return;
    const maxScrollTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    if (maxScrollTop <= 1) return;

    const deltaUnit = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? scroll.clientHeight
        : 1;
    scroll.scrollTop = Math.max(0, Math.min(maxScrollTop, scroll.scrollTop + event.deltaY * deltaUnit));
    scrollPositions[renderedTab] = scroll.scrollTop;
    updateScrollAffordance(scroll);
    event.preventDefault();
  }

  function onPointerDown(event: PointerEvent) {
    if (!event.isPrimary || event.pointerType !== "mouse" || event.button !== 0) return;
    const target = event.target instanceof HTMLElement ? event.target : undefined;
    if (!target || target.closest("input,textarea,select,[contenteditable='true'],[data-no-drag-scroll]")) return;
    const scroll = target.closest<HTMLElement>(".apk-entitlement-scroll");
    if (!scroll || scroll.scrollHeight <= scroll.clientHeight + 1) return;
    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollTop: scroll.scrollTop,
      moved: false,
      scroll
    };
  }

  function onPointerMove(event: PointerEvent) {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (!dragState.moved) {
      if (Math.hypot(deltaX, deltaY) < 7) return;
      if (Math.abs(deltaX) >= Math.abs(deltaY)) {
        dragState = undefined;
        return;
      }
      dragState.moved = true;
      dragState.scroll.dataset.dragging = "true";
    }

    dragState.scroll.scrollTop = dragState.startScrollTop - deltaY;
    scrollPositions[renderedTab] = dragState.scroll.scrollTop;
    updateScrollAffordance(dragState.scroll);
    event.preventDefault();
  }

  function finishPointerDrag(event: PointerEvent) {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    dragState.scroll.removeAttribute("data-dragging");
    if (dragState.moved) suppressClickUntil = Date.now() + 350;
    dragState = undefined;
  }

  root.addEventListener("scroll", onScroll, true);
  root.addEventListener("wheel", onWheel, { passive: false });
  root.addEventListener("pointerdown", onPointerDown);
  root.addEventListener("pointermove", onPointerMove, { passive: false });
  root.addEventListener("pointerup", finishPointerDrag);
  root.addEventListener("pointercancel", finishPointerDrag);
  root.addEventListener("click", (event) => {
    if (Date.now() > suppressClickUntil) return;
    suppressClickUntil = 0;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  root.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    const closeButton = target?.closest<HTMLElement>("[data-action='close']");
    if (closeButton) {
      close();
      return;
    }

    const tabButton = target?.closest<HTMLElement>("[data-tab]");
    if (tabButton) {
      activeTab = tabButton.dataset.tab as EntitlementTab;
      status = "";
      render();
      return;
    }

    const authButton = target?.closest<HTMLElement>("[data-action='auth']");
    if (authButton) {
      close();
      options.onRequireAuth?.();
      return;
    }

    const methodButton = target?.closest<HTMLElement>("[data-method-id]");
    if (methodButton) {
      selectedMethodId = Number(methodButton.dataset.methodId) || 0;
      render();
      return;
    }

    const modelPrimaryButton = target?.closest<HTMLElement>("[data-model-primary-id]");
    if (modelPrimaryButton) {
      activeModelPrimaryId = Number(modelPrimaryButton.dataset.modelPrimaryId) || 0;
      render();
      return;
    }

    const modelButton = target?.closest<HTMLElement>("[data-model-id]");
    if (modelButton) {
      const modelId = Number(modelButton.dataset.modelId) || 0;
      const model = flattenModelGroups(options.modelCenter?.groups ?? []).find((item) => item.id === modelId);
      if (!model || !isModelCallable(model)) return;
      // 所有游戏共用同一份 package 级模型偏好，避免刷新后回落到已停用旧模型。
      persistCurrentGameModelId(model.id);
      options.modelCenter?.onModelSelected?.(model);
      activeSelectedModelId = model.id;
      activeSelectedModelName = (options.modelCenter?.modelTitle ?? defaultModelTitle)(model);
      render();
      return;
    }

    const productButton = target?.closest<HTMLElement>("[data-product-id]");
    if (productButton) {
      selectedKind = productButton.dataset.kind as PurchaseKind;
      selectedProductId = Number(productButton.dataset.productId) || 0;
      render();
      return;
    }

    const payButton = target?.closest<HTMLElement>("[data-action='pay']");
    if (!payButton) return;
    const kind = currentKind();
    const product = selectedProduct();
    if (!product || busy) return;
    if (!canCreatePaymentOrder() || !options.token) {
      close();
      options.onRequireAuth?.();
      return;
    }
    busy = true;
    status = "正在创建订单...";
    render();
    CommerceClient.createPaymentOrder({
      token: options.token,
      productType: kind,
      productId: product.id,
      paymentMethodId: selectedMethodId,
      device: "h5"
    })
      .then((order) => {
        options.onOrderCreated?.(order);
        status = order.status === "paid" || order.status === "completed" ? "订单已完成，请刷新账号权益。" : "订单已创建，请完成支付后回到游戏。";
        openPayment(order);
      })
      .catch((error: unknown) => {
        status = error instanceof Error ? error.message : "订单创建失败，请稍后再试。";
      })
      .finally(() => {
        busy = false;
        render();
      });
  });

  document.addEventListener("keydown", onKeydown);

  render();
  Promise.all([
    CommerceClient.bootstrap(),
    CommerceClient.listRechargePackages("moon").catch(() => [] as RechargePackage[]),
    CommerceClient.listPaymentMethods().catch(() => [] as PaymentMethod[])
  ])
    .then(([bootstrap, rechargeItems, paymentMethods]) => {
      products = visibleMembershipProducts(bootstrap.membership_plans ?? []);
      rechargePackages = visibleRechargePackages(rechargeItems);
      methods = paymentMethods.filter((method) => method.enabled !== false).sort((left, right) => (right.sort ?? 0) - (left.sort ?? 0) || left.id - right.id);
      selectedMethodId = methods[0]?.id ?? 0;
      status = products.length || rechargePackages.length ? "" : "暂无可售会员商品，请稍后再试。";
      render();
    })
    .catch((error: unknown) => {
      status = error instanceof Error ? error.message : "会员商品加载失败，请稍后再试。";
      render();
    });

  return { close };
  /* c8 ignore stop */
}
